import React, { useCallback, useState } from 'react';
import { Spin, notification } from 'antd';
import opportunityInfoServices from '../../../../services/oneButtonServices/opportunityInfoService';
import DashboardModal from './DashboardModal';
import './oneButton-css/ObdGoogleStreetView.css';

type InstallationAddressLike = {
    Latitude?: number | string;
    latitude?: number | string;
    Longitude?: number | string;
    longitude?: number | string;
};

function readCoordsFromRoot(root: unknown): { lat?: number | string; lng?: number | string } {
    const r = root as Record<string, unknown> | null | undefined;
    const addr = (r?.InstallationAddress ?? r?.installationAddress ?? {}) as InstallationAddressLike;
    return {
        lat: addr.Latitude ?? addr.latitude,
        lng: addr.Longitude ?? addr.longitude,
    };
}

export function useObdGoogleStreetView(theme: 'dark' | 'light') {
    const [streetViewOpen, setStreetViewOpen] = useState(false);
    const [streetViewUrl, setStreetViewUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const openWithCoords = useCallback(
        async (lat: number | string | undefined, lng: number | string | undefined) => {
            if (loading) return;
            if (lat == null || lat === '' || lng == null || lng === '') {
                notification.warning({
                    message: 'Incomplete Address',
                    description: 'Valid latitude and longitude are required for street view.',
                    placement: 'bottomLeft',
                    className: `obd-notification obd-notification-warning obd-theme-${theme}`,
                });
                return;
            }

            setLoading(true);
            try {
                const res = await opportunityInfoServices.getGoogleStreet(lat, lng);
                if (res?.data) {
                    setStreetViewUrl(res.data);
                    setStreetViewOpen(true);
                } else {
                    notification.error({
                        message: 'Street View Unavailable',
                        description: 'No street view data found for this location.',
                        placement: 'bottomLeft',
                        className: `obd-notification obd-notification-error obd-theme-${theme}`,
                    });
                }
            } catch (err) {
                console.error('Error fetching street view:', err);
                notification.error({
                    message: 'Error',
                    description: 'Failed to load Google Street View.',
                    placement: 'bottomLeft',
                    className: `obd-notification obd-notification-error obd-theme-${theme}`,
                });
            } finally {
                setLoading(false);
            }
        },
        [loading, theme],
    );

    const openFromRoot = useCallback(
        async (root: unknown) => {
            const { lat, lng } = readCoordsFromRoot(root);
            await openWithCoords(lat, lng);
        },
        [openWithCoords],
    );

    const streetViewModal = (
        <DashboardModal
            open={streetViewOpen}
            onClose={() => setStreetViewOpen(false)}
            footer={null}
            title="Google Street View"
            width={1000}
            centered
            theme={theme}
            destroyOnClose
            className={`obd-street-view-modal obd-theme-${theme}`}
            rootClassName={`obd-street-view-modal-root obd-theme-${theme}`}
        >
            <div className="obd-street-view-modal__frame-wrap">
                {streetViewUrl ? (
                    <iframe
                        src={streetViewUrl}
                        width="100%"
                        height="100%"
                        style={{ border: 'none' }}
                        title="Google Street View"
                        allowFullScreen
                    />
                ) : (
                    <div className="obd-street-view-modal__loading">
                        <Spin tip="Loading Street View..." />
                    </div>
                )}
            </div>
        </DashboardModal>
    );

    return {
        loading,
        openWithCoords,
        openFromRoot,
        streetViewModal,
    };
}
